import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { AiChatSettingsSchema, DEFAULT_CHAT_WINDOWS, RequestFormSettingsSchema, paths, type AiChatSettings, type RequestServiceType } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { textareaClass } from "./JobEditCard";
import { useEntitlements } from "../hooks/useEntitlements";
import { PlanLocked } from "./PlanLocked";

// Requests → AI chat scheduling: the chat bubble on the company's website
// that books visits into Requests (pending approval). Every knob is the
// company's: its name, words, services, arrival windows, days, how many a
// day. Produces the code the web designer pastes in.

const APP = "https://app.clevis.pro";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function chatSnippet(slug: string, label: string, color: string): string {
  return `<script>
(function () {
  var APP = "${APP}", slug = "${slug}";
  var b = document.createElement("button"); b.type = "button"; b.textContent = ${JSON.stringify(label)};
  b.style.cssText = "position:fixed;right:20px;bottom:20px;z-index:2147483000;border:0;border-radius:999px;padding:12px 18px;background:${color};color:#fff;font:600 15px system-ui,sans-serif;box-shadow:0 6px 24px rgba(0,0,0,.2);cursor:pointer";
  var f = document.createElement("iframe"); f.title = "Chat with us"; f.loading = "lazy";
  f.src = APP + "/chat/" + slug + "?embed=1&page=" + encodeURIComponent(location.pathname);
  f.style.cssText = "position:fixed;right:20px;bottom:76px;width:380px;max-width:calc(100vw - 40px);height:560px;max-height:calc(100vh - 100px);border:0;border-radius:16px;box-shadow:0 12px 40px rgba(0,0,0,.25);z-index:2147483000;display:none;background:#fff";
  b.onclick = function () { var open = f.style.display !== "none"; f.style.display = open ? "none" : "block"; b.textContent = open ? ${JSON.stringify(label)} : "Close"; };
  document.body.appendChild(f); document.body.appendChild(b);
})();
</script>`;
}

function CopyBox({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <button
          className="text-xs font-semibold text-brandorange-dark hover:text-brandorange"
          onClick={() => {
            void navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "copied ✓" : "copy"}
        </button>
      </div>
      <pre className="max-h-48 overflow-auto rounded-lg border border-steel/20 bg-cream/50 p-2 text-[11px] leading-snug text-ink">{text}</pre>
    </div>
  );
}

export function AiChatSetup({ tenantId, slug, company, canEdit }: { tenantId: string; slug: string; company: string; canEdit: boolean }) {
  const [form, setForm] = useState<AiChatSettings | null>(null);
  const [types, setTypes] = useState<RequestServiceType[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const chatOn = useEntitlements().hasFeature("aiChat");

  useEffect(() => {
    const subs = [
      onSnapshot(doc(db, paths.tenantAiChatSettings(tenantId)), (snap) => setForm((f) => f ?? AiChatSettingsSchema.parse(snap.data() ?? {}))),
      onSnapshot(doc(db, paths.tenantRequestFormSettings(tenantId)), (snap) => setTypes(RequestFormSettingsSchema.parse(snap.data() ?? {}).serviceTypes)),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  if (!form) return null;
  const set = (patch: Partial<AiChatSettings>) => setForm({ ...form, ...patch });
  const setWindow = (i: number, patch: Partial<AiChatSettings["windows"][number]>) => set({ windows: form.windows.map((w, j) => (j === i ? { ...w, ...patch } : w)) });

  async function save() {
    if (!form) return;
    setBusy(true);
    setNote("");
    try {
      const { updatedAt: _u, ...settings } = form;
      await call("setAiChatSettings", { tenantId, settings });
      setNote("Saved.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-4">
      <div>
        <CardTitle>AI chat scheduling</CardTitle>
        <p className="text-xs text-steel">
          A chat bubble on your website that books visits. It only offers your services, your arrival windows and days with room; every
          booking lands here in Requests, pending, until you approve it. Nothing goes on the calendar on its own.
        </p>
      </div>
      {!chatOn ? <PlanLocked feature="aiChat" /> : null}
      <fieldset disabled={!canEdit || !chatOn} className={chatOn ? "space-y-4" : "space-y-4 opacity-50"}>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
          <strong>On</strong> — the bubble works on your site and at {APP.replace("https://", "")}/chat/{slug}
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Its name</Label>
            <Input value={form.assistantName} placeholder={`${company}'s assistant`} onChange={(e) => set({ assistantName: e.target.value })} />
          </div>
          <div>
            <Label>Button on your site</Label>
            <div className="flex gap-2">
              <Input value={form.buttonLabel} onChange={(e) => set({ buttonLabel: e.target.value })} />
              <input type="color" className="h-9 w-12 rounded border border-steel/30" value={form.color} onChange={(e) => set({ color: e.target.value })} aria-label="Button color" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>First thing it says</Label>
            <Input value={form.greeting} placeholder={`Hi! I can set up a visit with ${company}. What do you need done?`} onChange={(e) => set({ greeting: e.target.value })} />
          </div>
        </div>

        <div>
          <Label>Arrival windows it may offer (your time)</Label>
          <div className="space-y-1">
            {form.windows.map((w, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
                <Input className="w-28 py-1 text-xs" value={w.label} onChange={(e) => setWindow(i, { label: e.target.value })} placeholder="8-10 AM" />
                <Input type="time" className="w-28 py-1 text-xs" value={w.start} onChange={(e) => setWindow(i, { start: e.target.value })} />
                <span className="text-steel">to</span>
                <Input type="time" className="w-28 py-1 text-xs" value={w.end} onChange={(e) => setWindow(i, { end: e.target.value })} />
                {form.windows.length > 1 ? (
                  <button type="button" className="text-steel hover:text-danger" onClick={() => set({ windows: form.windows.filter((_, j) => j !== i) })}>
                    remove
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          <div className="mt-1 flex gap-3 text-xs">
            {form.windows.length < 8 ? (
              <button type="button" className="text-brandorange-dark underline" onClick={() => set({ windows: [...form.windows, { label: "", start: "09:00", end: "11:00" }] })}>
                Add a window
              </button>
            ) : null}
            <button type="button" className="text-steel underline" onClick={() => set({ windows: DEFAULT_CHAT_WINDOWS })}>
              Back to the usual four
            </button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Days it may book</Label>
            <div className="flex flex-wrap gap-2 text-xs">
              {DAYS.map((d, i) => (
                <label key={d} className="flex items-center gap-1">
                  <input type="checkbox" checked={form.days.includes(i)} onChange={(e) => set({ days: e.target.checked ? [...form.days, i] : form.days.filter((x) => x !== i) })} />
                  {d}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <label>
              Earliest
              <Input type="number" min="0" max="30" className="py-1 text-xs" value={form.leadDays} onChange={(e) => set({ leadDays: Math.max(0, parseInt(e.target.value || "0", 10)) })} />
              <span className="text-steel">days out</span>
            </label>
            <label>
              Up to
              <Input type="number" min="1" max="60" className="py-1 text-xs" value={form.horizonDays} onChange={(e) => set({ horizonDays: Math.max(1, parseInt(e.target.value || "1", 10)) })} />
              <span className="text-steel">days out</span>
            </label>
            <label>
              Full at
              <Input type="number" min="0" max="100" className="py-1 text-xs" value={form.maxPerDay} onChange={(e) => set({ maxPerDay: Math.max(0, parseInt(e.target.value || "0", 10)) })} />
              <span className="text-steel">visits a day (0 = no limit)</span>
            </label>
          </div>
        </div>

        {types.length ? (
          <div>
            <Label>Services it may book (none ticked = all)</Label>
            <div className="flex flex-wrap gap-2 text-xs">
              {types.map((t) => (
                <label key={t.id} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={form.serviceTypeIds.includes(t.id)}
                    onChange={(e) => set({ serviceTypeIds: e.target.checked ? [...form.serviceTypeIds, t.id] : form.serviceTypeIds.filter((x) => x !== t.id) })}
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-steel">Add your service types under Website form and the chat will offer them.</p>
        )}

        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={form.askAddress} onChange={(e) => set({ askAddress: e.target.checked })} />
          Ask for the service address
        </label>
        <div>
          <Label>Anything it should know or say (your words)</Label>
          <textarea className={textareaClass} rows={3} value={form.instructions} placeholder="We don't service commercial doors. Same-day calls: phone the office." onChange={(e) => set({ instructions: e.target.value })} />
        </div>
        <div className="flex items-center gap-2">
          <Button busy={busy} onClick={() => void save()}>
            Save chat settings
          </Button>
          {note ? <span className="text-xs text-steel">{note}</span> : null}
          <a href={`${APP}/chat/${slug}`} target="_blank" rel="noreferrer" className="text-xs text-brandorange-dark underline">
            Try it
          </a>
        </div>
      </fieldset>
      <CopyBox label="Code for your web designer (paste before </body>)" text={chatSnippet(slug, form.buttonLabel || "Chat with us", form.color)} />
    </Card>
  );
}
